import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ASSETS } from '../../assetManifest';
import { CONSTANTS } from '../../data';
import type { AssetsSchema } from '../../assetManifestTypes';

interface SplashScreenProps {
  onComplete: () => void;
}

export const SplashScreen: React.FC<SplashScreenProps> = ({ onComplete }) => {
  const splash = CONSTANTS.SPLASH;
  
  const [shouldFadeOut, setShouldFadeOut] = useState(false);

  const typedAssets = ASSETS as AssetsSchema;
  const category = splash.imageCategory;
  const key = splash.imageKey;
  const asset = (typedAssets[category] && typedAssets[category][key]) || {
    path: '', // Default path can be empty as it should be defined
    width: 256,
    height: 256,
  };

  const width = splash.imageDisplayWidth || asset.width || 256;
  const height = splash.imageDisplayHeight || asset.height || 256;

  const bgColor = splash.backgroundColor || '#000000';
  const textColor = splash.textColor || '#ffffff';

  const gridColor = splash.gridColor;
  const gridDotSize = splash.gridDotSize;
  const gridSpacing = splash.gridSpacing;

  useEffect(() => {
    const fadeOutTimer = setTimeout(() => {
      setShouldFadeOut(true);
    }, splash.fadeOutDelayMs);

    // The total duration before calling onComplete includes the fade-out time.
    const completeTimer = setTimeout(() => {
      onComplete();
    }, splash.fadeOutDelayMs + splash.fadeOutDurationMs);

    return () => {
      clearTimeout(fadeOutTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete, splash.fadeOutDelayMs, splash.fadeOutDurationMs]);

  const handleImageClick = () => {
    window.open('https://graph.plus', '_blank');
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center"
      style={{
        backgroundColor: bgColor,
        color: textColor,
        backgroundImage: `radial-gradient(circle, ${gridColor} ${gridDotSize}px, transparent ${gridDotSize}px)`,
        backgroundSize: `${gridSpacing}px ${gridSpacing}px`,
        backgroundPosition: '50% 50%'
      }}
    >
      <div className="flex flex-col items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ 
            opacity: shouldFadeOut ? 0 : 1, 
            y: 0,
            scale: shouldFadeOut ? splash.fadeOutScaleTo : 1
          }}
          transition={{ 
            duration: shouldFadeOut ? splash.fadeOutDurationMs / 1000 : 0.4,
            ease: 'easeOut'
          }}
        >
          <p className="uppercase tracking-widest text-sm opacity-80">
            {splash.text}
          </p>
        </motion.div>

        <motion.img
          src={asset.path}
          alt="Splash Logo"
          width={width}
          height={height}
          className="mt-3 drop-shadow-xl cursor-pointer"
          initial={{ scale: splash.popScaleFrom }}
          animate={{
            scale: shouldFadeOut ? splash.fadeOutScaleTo : [
              splash.popScaleFrom,
              splash.popOvershoot,
              splash.popScaleTo,
            ],
            opacity: shouldFadeOut ? 0 : 1
          }}
          transition={{
            duration: shouldFadeOut ? splash.fadeOutDurationMs / 1000 : splash.popDurationMs / 1000,
            times: shouldFadeOut ? undefined : [0, 0.6, 1],
            ease: 'easeOut',
          }}
          onClick={handleImageClick}
        />
      </div>
    </div>
  );
};
